import React, { useState } from "react";
import axios from "axios";
import { useTheme } from "../../theme";

const STEP_LABELS = ['account_url', 'date_met', 'additional_accts', 'additional_info'];
const STEP_TITLES = [
  "What's their account URL?",
  "When did you meet them?",
  "Any additional accounts?",
  "Anything else you want to add?",
];

const steamRegex = /https:\/\/steamcommunity\.com\/id\//;
const playstationRegex = /https:\/\/psnprofiles\.com\//;
const xboxRegex = /https:\/\/account\.xbox\.com/;

const CreateTag = () => {
  const { p, d, fonts } = useTheme();

  const [activeStep, setActiveStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState('');

  const [accountLink, setAccountLink] = useState('');
  const [accountType, setAccountType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [s3links, setS3links] = useState({ steam: '', playstation: '', xbox: '', discord: '' });
  const [additionalInfo, setAdditionalInfo] = useState('');

  const detectPlatform = (link) => {
    if (steamRegex.test(link)) return 'steam';
    if (playstationRegex.test(link)) return 'playstation';
    if (xboxRegex.test(link)) return 'xbox';
    return null;
  };

  const handleNext = () => {
    setError('');
    if (activeStep === 0) {
      const platform = detectPlatform(accountLink);
      if (!platform) { setError('Please enter a valid Steam, PlayStation, or Xbox profile URL'); return; }
      setAccountType(platform);
      setActiveStep(s => s + 1);
    } else if (activeStep === 3) {
      axios
        .post(`${process.env.REACT_APP_API_URL}/tags/`, {
          findee_account_link: accountLink,
          findee_account_type: accountType,
          date_met: selectedDate || null,
          additional_steam: s3links.steam || null,
          additional_psn: s3links.playstation || null,
          additional_xbox: s3links.xbox || null,
          additional_discord: s3links.discord || null,
          additional_info: additionalInfo || null,
        }, {
          headers: { Authorization: `token ${localStorage.getItem("token")}` },
        })
        .catch((err) => console.error("Tag submission error:", err));
      setFinished(true);
    } else {
      setActiveStep(s => s + 1);
    }
  };

  const handleBack = () => { setError(''); setActiveStep(s => s - 1); };

  const inputStyle = {
    width: '100%', background: 'transparent', border: 'none', outline: 'none',
    fontFamily: fonts.mono, fontSize: 16, color: p.textHi, caretColor: p.accent, padding: '16px 0',
  };

  const otherPlatforms = ['steam', 'playstation', 'xbox', 'discord'].filter(x => x !== accountType);

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <>
          <div style={{
            border: `1px solid ${error ? p.rose : p.accent}`, background: p.bgPanel,
            padding: '4px 4px 4px 22px', display: 'flex', alignItems: 'center', gap: 14,
            boxShadow: `0 0 0 4px ${p.accentBg}`,
          }}>
            <span style={{ color: p.accent, fontFamily: fonts.mono, fontSize: 16 }}>↗</span>
            <input
              value={accountLink}
              onChange={e => setAccountLink(e.target.value)}
              placeholder="https://steamcommunity.com/id/kestrel_99"
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
          {error && <div style={{ fontFamily: fonts.mono, fontSize: 11, color: p.rose, marginTop: 10 }}>{error}</div>}
          <p style={{ textAlign: 'center', fontSize: 13.5, color: p.textDim, marginTop: 18, lineHeight: 1.5 }}>
            Enter a Steam, Xbox, or PlayStation Network account URL.<br />
            We'll handle the easy stuff: extracting the handle and confirming the platform.
          </p>
          <div style={{
            marginTop: 36, padding: '18px 22px', border: `1px dashed ${p.border}`, background: p.bgPanel,
            fontFamily: fonts.mono, fontSize: 11.5, color: p.textDim, lineHeight: 1.7, letterSpacing: '.04em',
          }}>
            <div style={{ color: p.text, marginBottom: 6 }}># supported formats</div>
            <div>steamcommunity.com/id/USERNAME</div>
            <div>psnprofiles.com/USERNAME</div>
            <div>account.xbox.com/...USERNAME</div>
          </div>
        </>
      );
    }

    if (activeStep === 1) {
      return (
        <>
          <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>
            YEAR AND MONTH YOU MET
          </label>
          <div style={{
            border: `1px solid ${p.borderHi}`, padding: '13px 14px',
            fontFamily: fonts.mono, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ color: p.accent }}>›</span>
            <input
              type="month"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              min="2003-01"
              max={new Date().toISOString().slice(0, 7)}
              style={{ ...inputStyle, fontSize: 14, padding: '6px 0', colorScheme: 'dark' }}
            />
          </div>
          <p style={{ textAlign: 'center', fontSize: 13.5, color: p.textDim, marginTop: 18, lineHeight: 1.5 }}>
            Enter the year and month you met them. It doesn't have to be spot-on.
          </p>
        </>
      );
    }

    if (activeStep === 2) {
      return (
        <>
          <p style={{ fontSize: 13.5, color: p.textDim, marginBottom: 24, lineHeight: 1.5, textAlign: 'center' }}>
            Enter any additional accounts they may have. Optional.
          </p>
          {otherPlatforms.map(plat => (
            <div key={plat} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>
                {plat.toUpperCase()}
              </label>
              <div style={{
                border: `1px solid ${p.borderHi}`, padding: '13px 14px',
                fontFamily: fonts.mono, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ color: p.accent }}>›</span>
                <input
                  value={s3links[plat]}
                  onChange={e => setS3links(prev => ({ ...prev, [plat]: e.target.value }))}
                  placeholder={`${plat} username or link`}
                  style={{ ...inputStyle, fontSize: 14, padding: '6px 0' }}
                />
              </div>
            </div>
          ))}
        </>
      );
    }

    // step 3: additional info
    return (
      <>
        <label style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.14em', color: p.textDim, textTransform: 'uppercase', marginBottom: 8 }}>
          ADDITIONAL INFO (OPTIONAL)
        </label>
        <textarea
          value={additionalInfo}
          onChange={e => setAdditionalInfo(e.target.value)}
          maxLength={150}
          rows={4}
          placeholder="a memory, a game, a year. something only they'd recognize..."
          style={{
            width: '100%', background: 'transparent',
            border: `1px solid ${p.borderHi}`, padding: '14px',
            fontFamily: fonts.sans, fontSize: 14, color: p.textHi,
            caretColor: p.accent, outline: 'none', resize: 'vertical',
          }}
        />
        <div style={{ fontFamily: fonts.mono, fontSize: 11, color: p.textDim, marginTop: 6, textAlign: 'right' }}>
          {additionalInfo.length} / 150
        </div>
        <p style={{ textAlign: 'center', fontSize: 13.5, color: p.textDim, marginTop: 12, lineHeight: 1.5 }}>
          Remember our golden rule: only add information about your relationship, not about them.
        </p>
      </>
    );
  };

  if (finished) {
    return (
      <div style={{ minHeight: '100%', background: p.bg, color: p.text, fontFamily: fonts.sans }}>
        <section style={{ padding: `${d.hero}px ${d.pad}px`, textAlign: 'center' }}>
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 11, color: p.accent, letterSpacing: '.18em', marginBottom: 24 }}>
              § /file · complete
            </div>
            <h1 style={{
              fontFamily: fonts.serif, fontSize: 48, fontWeight: 400,
              letterSpacing: '-.02em', color: p.textHi, lineHeight: 1.05, margin: '0 0 16px',
            }}>
              You're done!
            </h1>
            <p style={{ fontSize: d.body, color: p.text, lineHeight: 1.55, margin: '0 0 36px' }}>
              Your tag has been filed. When they search their old name, your note will appear. Thank you for being part of the index.
            </p>
            <span
              onClick={() => window.location.href = '/'}
              style={{
                padding: '14px 28px', background: p.accent, color: p.bg,
                fontFamily: fonts.mono, fontSize: 13, fontWeight: 600, letterSpacing: '.06em', cursor: 'pointer',
              }}
            >DONE ↵</span>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100%', background: p.bg, color: p.text, fontFamily: fonts.sans }}>
      <section style={{ padding: `${d.gap * 3}px ${d.pad}px`, maxWidth: 880, margin: '0 auto', borderBottom: `1px solid ${p.border}` }}>
        <div style={{
          fontFamily: fonts.mono, fontSize: 11, color: p.textDim,
          letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 12, textAlign: 'center',
        }}>
          § /file · step {activeStep + 1} of 4 · {STEP_LABELS[activeStep]}
        </div>
        <h1 style={{
          fontFamily: fonts.serif, fontSize: 48, fontWeight: 400, letterSpacing: '-.02em',
          textAlign: 'center', margin: '0 0 36px', color: p.textHi, lineHeight: 1.05,
        }}>
          {STEP_TITLES[activeStep]}
        </h1>

        {/* progress rail */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 0, marginBottom: 48,
          fontFamily: fonts.mono, fontSize: 10, letterSpacing: '.1em',
        }}>
          {STEP_LABELS.map((s, i) => (
            <React.Fragment key={s}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 18, height: 18,
                  border: `1px solid ${i <= activeStep ? p.accent : p.borderHi}`,
                  background: i < activeStep ? p.accent : 'transparent',
                  display: 'grid', placeItems: 'center',
                  color: i < activeStep ? p.bg : (i === activeStep ? p.accent : p.textFaint),
                  fontSize: 10,
                }}>{i < activeStep ? '✓' : String(i + 1).padStart(2, '0')}</span>
                <span style={{
                  color: i <= activeStep ? p.textHi : p.textFaint, textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}>{s.replace(/_/g, ' ')}</span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <span style={{ flex: 1, height: 1, background: i < activeStep ? p.accent : p.border, margin: '0 16px' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {renderStepContent()}

        {/* nav buttons */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', marginTop: 64,
          fontFamily: fonts.mono, fontSize: 12, letterSpacing: '.08em',
        }}>
          <span
            onClick={activeStep > 0 ? handleBack : undefined}
            style={{ color: activeStep > 0 ? p.textDim : 'transparent', cursor: activeStep > 0 ? 'pointer' : 'default' }}
          >← BACK</span>
          <span onClick={handleNext} style={{ color: p.accent, cursor: 'pointer' }}>
            {activeStep === 3 ? 'FINISH ↵' : 'NEXT →'}
          </span>
        </div>
      </section>
    </div>
  );
};

export default CreateTag;
